import React from "react"
import { useTranslation } from "react-i18next"

import { ContentTag } from "scrivito"
import { ProductObj } from "scrivito/Objs/Product/ProductObjClass"
import { ProductCategoryObj } from "scrivito/Objs/ProductCategory/ProductCategoryObjClass"
import { isEmpty } from "shared/lib/arrayUtils"
import { IconSize } from "shared/types/Icon"
import { SelectOption } from "shared/types/Select"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import {
  ProductFilterFormData,
  hasProductFilter
} from "entities/products/types/ProductFilter"
import { ProductCardLegend } from "entities/products/ui/product-card/ProductCardLegend"
import { ProductFilter } from "widgets/product-filter/ProductFilter"
import { ProductGrid } from "widgets/product-grid/ProductGrid"

import "./styles/_product-category-details.scss"

interface ProductCategoryDetailsModel {
  category: ProductCategoryObj
  products: ProductObj[] | undefined

  productGroupOptions: SelectOption<string>[] | undefined

  mode?: "link" | "button"
  onProductSelect?: (productObj: ProductObj) => void

  filter?: ProductFilterFormData
  defaultFilter?: ProductFilterFormData
  onFilterChange?: (filterState: ProductFilterFormData) => void

  onBack?: () => void
}

export const ProductCategoryDetails = React.memo(
  ({
    category,
    products,
    mode = "link",
    productGroupOptions,
    onProductSelect,
    filter,
    defaultFilter,
    onFilterChange,
    onBack
  }: ProductCategoryDetailsModel) => {
    const { t } = useTranslation()
    const showBuildingTypeFilter = category.get("showBuildingTypeFilter")
    const showRoomTypeFilter = category.get("showRoomTypeFilter")
    const showProductGroupFilter = (productGroupOptions?.length ?? 0) > 1

    const isSearchOnly =
      !showBuildingTypeFilter && !showRoomTypeFilter && !showProductGroupFilter

    const emptyMessage =
      filter && hasProductFilter(filter)
        ? t("Product.NoResults")
        : t("Product.Empty")

    return (
      <div className="product-category-details">
        <div className="product-category-details__header">
          <div className="product-category-details__title-and-back">
            {onBack ? (
              <Button
                isIconOnly
                icon={IconName.ARROW_BACK}
                variant={ButtonVariant.TERTIARY}
                iconSize={IconSize.LARGE}
                onClick={onBack}
                label={t("General.Back")}
                className="product-category-details__back"
              />
            ) : null}
            <ContentTag
              tag="h1"
              content={category}
              attribute="title"
              className="product-category-details__title"
            />
          </div>
          {isSearchOnly ? (
            <ProductFilter.Root
              defaultValues={defaultFilter}
              onChange={onFilterChange}
            >
              <ProductFilter.Search className="input--elevated" />
            </ProductFilter.Root>
          ) : null}
        </div>
        {!isSearchOnly ? (
          <ProductFilter.Root
            defaultValues={defaultFilter}
            onChange={onFilterChange}
          >
            <div className="product-category-details__filters">
              {showProductGroupFilter ? (
                <ProductFilter.ProductGroup
                  elevated
                  options={productGroupOptions}
                  disabled={isEmpty(productGroupOptions)}
                />
              ) : null}
              {showBuildingTypeFilter ? (
                <ProductFilter.BuildingType elevated />
              ) : null}
              {showRoomTypeFilter ? <ProductFilter.RoomType elevated /> : null}

              <ProductFilter.Search className="input--elevated" />
            </div>
            <ProductFilter.ChipList productGroupOptions={productGroupOptions} />
          </ProductFilter.Root>
        ) : null}
        <ProductCardLegend />
        <ProductGrid
          productObjs={products}
          mode={mode}
          onSelect={onProductSelect}
          emptyMessage={emptyMessage}
        />
      </div>
    )
  }
)
